import Card from '@mui/material/Card';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import type { JSX } from 'react';
import { useFormContext } from 'react-hook-form';

import AutocompleteTaxRateSchedulesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteTaxRateSchedulesTypeahead';
import { TextFormField } from '~components/FormFields/TextFormField';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';

import type { ProjectFormSchemaInterface } from '../../projectFormSchema';
import { useProjectFormV2Context } from '../ProjectFormV2Context';

// Mirrors the legacy form. Render-only by design: panels
// stay free of reactive subscriptions — the Provider owns the
// taxRateSchedule observation so the `disabled` state of taxRate updates
// reactively when the schedule is picked or cleared. The auto-populate
// effect lives in ProjectFormV2Context (mirrors the legacy form).
//
// Field set rendered by this panel:
//   taxRateSchedule   — typeahead bound to the watched company id
//   taxRate           — number; disabled while a schedule is selected
//   taxFreight        — checkbox
//   taxMaterial       — checkbox
//   taxAddOns         — checkbox
export function TaxPanel(): JSX.Element {
  const { control, formState, trigger } = useFormContext<ProjectFormSchemaInterface>();
  const { flags, isTaxRequired, watchedSelectedCompany, watchedTaxRateSchedule } =
    useProjectFormV2Context();
  const taxableScopeError = formState.errors.taxFreight?.message;
  // The scope error lives on taxFreight, so changing a sibling field must re-check it.
  const revalidateTaxableScope = () => {
    if (taxableScopeError) {
      void trigger('taxFreight');
    }
  };

  return (
    <Card sx={{ p: 2, mb: 2 }} data-test-id="project-form-v2-tax">
      <Typography variant="form-section-title">{t('project.form.tax')}</Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 6 }}>
          <AutocompleteTaxRateSchedulesTypeahead
            key={watchedSelectedCompany?.id}
            control={control}
            name="taxRateSchedule"
            label={t('project.form.tax_rate_schedule')}
            isRequired={flags.fastWeighEnabled || isTaxRequired}
            companyId={watchedSelectedCompany?.id || ''}
            onValueChange={revalidateTaxableScope}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <TextFormField
            control={control}
            disabled={Boolean(watchedTaxRateSchedule)}
            errors={formState.errors}
            label={t('project.form.tax_rate')}
            name="taxRate"
            type="number"
            inputProps={{ step: '0.0001' }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2.5 }}>
        <Grid size={{ xs: 4 }}>
          <CheckboxInput
            control={control}
            label={t('project.form.taxable_freight')}
            name="taxFreight"
            onValueChange={revalidateTaxableScope}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <CheckboxInput
            control={control}
            label={t('project.form.taxable_material')}
            name="taxMaterial"
            onValueChange={revalidateTaxableScope}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <CheckboxInput
            control={control}
            label={t('project.form.taxable_add_ons')}
            name="taxAddOns"
            onValueChange={revalidateTaxableScope}
          />
        </Grid>
      </Grid>

      {taxableScopeError && (
        <FormHelperText error sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
          {taxableScopeError}
        </FormHelperText>
      )}
    </Card>
  );
}
